import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';

const AUDIENCES_PAGE_SIZE = 50;

type AudienceReachability = {
	has_valid_email: boolean;
	email_count: number;
};

type AudienceSummaryResponse = {
	id: string;
	name: string;
	description?: string;
	member_count?: number;
	reachability?: AudienceReachability;
};

type AudienceRef = {
	id: string;
	name: string;
	memberCount: number;
};

type PaginatedAudiencesResponse = {
	audiences: AudienceSummaryResponse[];
	continuation_token?: string | null;
	has_more: boolean;
};

export function useAudiences(userId: string | undefined): {
	audiences: AudienceSummaryResponse[];
	audienceRefMap: Map<string, AudienceRef>;
	isLoading: boolean;
	isLoadingMore: boolean;
	hasMore: boolean;
	error: string | null;
	loadMore: () => void;
	refetch: () => void;
} {
	const bridgewayApi = useBridgewayApi();
	const [audiences, setAudiences] = useState<AudienceSummaryResponse[]>([]);
	const [internalLoading, setInternalLoading] = useState(Boolean(userId));
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [continuationToken, setContinuationToken] = useState<string | null>(
		null,
	);
	const [hasMore, setHasMore] = useState(false);
	const [resolvedUserId, setResolvedUserId] = useState<string | undefined>(
		undefined,
	);
	const requestIdRef = useRef(0);

	const fetchAudiencesPage = useCallback(
		async (token: string | null, append: boolean) => {
			if (!userId) return;

			const params = new URLSearchParams({
				page_size: String(AUDIENCES_PAGE_SIZE),
			});
			if (token) {
				params.set('continuation_token', token);
			}

			const response = await bridgewayApi.get<PaginatedAudiencesResponse>(
				`/api/user/${encodeURIComponent(userId)}/audiences/paginated?${params.toString()}`,
			);

			setAudiences((prev) =>
				append ? [...prev, ...response.audiences] : response.audiences,
			);
			setContinuationToken(response.continuation_token ?? null);
			setHasMore(response.has_more);
		},
		[bridgewayApi, userId],
	);

	const fetchAudiences = useCallback(async () => {
		if (!userId) {
			requestIdRef.current += 1;
			setAudiences([]);
			setError(null);
			setInternalLoading(false);
			setContinuationToken(null);
			setHasMore(false);
			setResolvedUserId(undefined);
			return;
		}

		const requestId = ++requestIdRef.current;
		setInternalLoading(true);
		setError(null);
		try {
			await fetchAudiencesPage(null, false);
			if (requestId !== requestIdRef.current) return;
			setResolvedUserId(userId);
		} catch (err) {
			if (requestId !== requestIdRef.current) return;
			setError(
				err instanceof Error ? err.message : 'Failed to fetch audiences',
			);
			setResolvedUserId(userId);
		} finally {
			if (requestId === requestIdRef.current) {
				setInternalLoading(false);
			}
		}
	}, [fetchAudiencesPage, userId]);

	const loadMore = useCallback(() => {
		if (!continuationToken || isLoadingMore) return;
		setIsLoadingMore(true);
		fetchAudiencesPage(continuationToken, true)
			.catch((err) => {
				setError(
					err instanceof Error ? err.message : 'Failed to load more audiences',
				);
			})
			.finally(() => {
				setIsLoadingMore(false);
			});
	}, [continuationToken, fetchAudiencesPage, isLoadingMore]);

	useEffect(() => {
		fetchAudiences();
	}, [fetchAudiences]);

	const isLoading =
		internalLoading || (Boolean(userId) && resolvedUserId !== userId);

	const audienceRefMap = useMemo(
		() =>
			new Map(
				audiences.map((a) => [
					a.id,
					{
						id: a.id,
						name: a.name,
						memberCount: a.member_count ?? 0,
					} satisfies AudienceRef,
				]),
			),
		[audiences],
	);

	return {
		audiences,
		audienceRefMap,
		isLoading,
		isLoadingMore,
		hasMore,
		error,
		loadMore,
		refetch: fetchAudiences,
	};
}
